import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../AuthContext';
import { parseDbTime, localDateFromYMD } from '../utils/dbTime';

function ReportsModal({ onClose, embedded = false }) {
  const { isOwner } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('week');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalLoads: 0,
    completedLoads: 0,
    pendingLoads: 0,
    cancelledLoads: 0,
    totalRevenue: 0,
    avgLoadValue: 0,
    topCustomers: [],
    driverPerformance: [],
    loadsByStatus: {},
    loadsByDay: [],
    guessedFields: []
  });

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    
    switch (dateRange) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case 'week':
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start.setMonth(start.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(start.getMonth() - 3);
        break;
      case 'year':
        start.setFullYear(start.getFullYear() - 1);
        break;
      default:
        start.setDate(start.getDate() - 7);
    }
    
    return { start, end };
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();

      const { data: loads, error } = await supabase
        .from('loads')
        .select(`
          *,
          customer:customers(company_name),
          driver:drivers(id, phone, user_id, user:user_id(name)),
          truck:trucks(truck_number)
        `)
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString());

      if (error) throw error;

      const completedLoads = loads.filter(l => l.status === 'complete');
      const pendingLoads = loads.filter(l => ['pending', 'assigned'].includes(l.status));
      const cancelledLoads = loads.filter(l => l.status === 'cancelled');
      
      const totalRevenue = completedLoads.reduce((sum, l) => sum + (l.rate || 0), 0);

      // Top customers by load count
      const customerCounts = {};
      loads.forEach(load => {
        const name = load.customer?.company_name || load.customer_name || 'Unknown';
        customerCounts[name] = (customerCounts[name] || 0) + 1;
      });
      const topCustomers = Object.entries(customerCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Driver performance
      const driverStats = {};
      loads.forEach(load => {
        if (load.driver_id) {
          const driverName = load.driver?.user?.name || load.driver?.phone || 'Unknown';
          if (!driverStats[driverName]) {
            driverStats[driverName] = { total: 0, completed: 0, onTime: 0 };
          }
          driverStats[driverName].total++;
          if (load.status === 'complete') {
            driverStats[driverName].completed++;
          }
        }
      });
      const driverPerformance = Object.entries(driverStats)
        .map(([name, stats]) => ({
          name,
          ...stats,
          completionRate: stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0
        }))
        .sort((a, b) => b.completed - a.completed);

      // Loads by status
      const loadsByStatus = {};
      loads.forEach(load => {
        loadsByStatus[load.status] = (loadsByStatus[load.status] || 0) + 1;
      });

      // Loads by day
      const loadsByDay = {};
      loads.forEach(load => {
        // loads.created_at is naive UTC. Bucketing it with new Date() put every
        // job created after 7-8pm local into the NEXT day's bar.
        const d = parseDbTime(load.created_at);
        if (!d) return;
        const day = d.toLocaleDateString('en-CA');
        loadsByDay[day] = (loadsByDay[day] || 0) + 1;
      });
      const loadsByDayArray = Object.entries(loadsByDay)
        .map(([date, count]) => ({ date, count }))
        // The keys are already 'YYYY-MM-DD', which sorts correctly as text -- and
        // without a Date parse that would read each one as UTC midnight.
        .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

      // Guessed fields (for owner oversight)
      const guessedFields = loads
        .filter(l => l.guessed_fields && l.guessed_fields.length > 0)
        .map(l => ({
          loadNumber: l.load_number,
          fields: l.guessed_fields,
          createdAt: l.created_at
        }));

      setStats({
        totalLoads: loads.length,
        completedLoads: completedLoads.length,
        pendingLoads: pendingLoads.length,
        cancelledLoads: cancelledLoads.length,
        totalRevenue,
        avgLoadValue: completedLoads.length > 0 ? totalRevenue / completedLoads.length : 0,
        topCustomers,
        driverPerformance,
        loadsByStatus,
        loadsByDay: loadsByDayArray,
        guessedFields
      });
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'var(--color-warning)',
      assigned: 'var(--accent-blue)',
      en_route: 'var(--color-info)',
      in_transit: '#2ecc71',
      complete: 'var(--color-success)',
      cancelled: 'var(--color-danger)'
    };
    return colors[status] || '#7f8c8d';
  };

  const exportToCSV = () => {
    const headers = ['Metric', 'Value'];
    const rows = [
      ['Total Loads', stats.totalLoads],
      ['Completed Loads', stats.completedLoads],
      ['Pending Loads', stats.pendingLoads],
      ['Cancelled Loads', stats.cancelledLoads],
      ['Total Revenue', formatCurrency(stats.totalRevenue)],
      ['Average Load Value', formatCurrency(stats.avgLoadValue)],
      ['', ''],
      ['Top Customers', ''],
      ...stats.topCustomers.map(c => [c.name, c.count + ' loads']),
      ['', ''],
      ['Driver Performance', ''],
      ...stats.driverPerformance.map(d => [d.name, d.completionRate + '% completion'])
    ];

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hmgc-report-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const content = (
    <div style={{
      backgroundColor: embedded ? 'transparent' : 'var(--surface-1)',
      borderRadius: '10px',
      padding: embedded ? '0' : '1.5rem',
      width: '100%',
      maxWidth: embedded ? '100%' : '1200px',
      maxHeight: embedded ? 'none' : '90vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: embedded ? 'visible' : 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid var(--border-default)',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h2 style={{ color: 'var(--accent-primary)', margin: '0 0 0.25rem 0' }}>
            Reports & Analytics
          </h2>
          <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.9rem' }}>
            Business intelligence and performance metrics
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          {/* Date range selector */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            style={{
              padding: '0.5rem',
              backgroundColor: 'var(--surface-2)',
              border: '1px solid var(--border-default)',
              borderRadius: '5px',
              color: 'var(--text-primary)'
            }}
          >
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last 90 Days</option>
            <option value="year">Last Year</option>
          </select>
          
          <button
            onClick={exportToCSV}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'var(--surface-2)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Export CSV
          </button>
          
          {!embedded && (
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-primary)',
                fontSize: '1.5rem',
                cursor: 'pointer'
              }}
            >
              x
            </button>
          )}
        </div>
      </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          <button
            onClick={() => setActiveTab('overview')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: activeTab === 'overview' ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('customers')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: activeTab === 'customers' ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Customers
          </button>
          <button
            onClick={() => setActiveTab('drivers')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: activeTab === 'drivers' ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Drivers
          </button>
          {isOwner && (
            <button
              onClick={() => setActiveTab('oversight')}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: activeTab === 'oversight' ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Owner Oversight
            </button>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              Loading report data...
            </div>
          ) : activeTab === 'overview' ? (
            <div>
              {/* KPI Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  backgroundColor: 'var(--surface-2)',
                  borderRadius: '8px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                    Total Loads
                  </div>
                  <div style={{ color: 'var(--accent-primary)', fontSize: '2rem', fontWeight: 'bold' }}>
                    {stats.totalLoads}
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'var(--surface-2)',
                  borderRadius: '8px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                    Completed
                  </div>
                  <div style={{ color: 'var(--color-success)', fontSize: '2rem', fontWeight: 'bold' }}>
                    {stats.completedLoads}
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'var(--surface-2)',
                  borderRadius: '8px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                    Total Revenue
                  </div>
                  <div style={{ color: 'var(--color-warning)', fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {formatCurrency(stats.totalRevenue)}
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'var(--surface-2)',
                  borderRadius: '8px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                    Avg Load Value
                  </div>
                  <div style={{ color: 'var(--accent-blue)', fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {formatCurrency(stats.avgLoadValue)}
                  </div>
                </div>
              </div>

              {/* Status breakdown */}
              <div style={{
                backgroundColor: 'var(--surface-2)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0' }}>Loads by Status</h3>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {Object.entries(stats.loadsByStatus).map(([status, count]) => (
                    <div
                      key={status}
                      style={{
                        backgroundColor: getStatusColor(status),
                        color: 'var(--text-primary)',
                        padding: '0.5rem 1rem',
                        borderRadius: '20px',
                        fontSize: '0.9rem'
                      }}
                    >
                      {status.replace(/_/g, ' ')}: {count}
                    </div>
                  ))}
                </div>
              </div>

              {/* Daily trend */}
              <div style={{
                backgroundColor: 'var(--surface-2)',
                borderRadius: '8px',
                padding: '1rem'
              }}>
                <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0' }}>Daily Load Volume</h3>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '150px' }}>
                  {stats.loadsByDay.map((day, index) => {
                    const maxCount = Math.max(...stats.loadsByDay.map(d => d.count), 1);
                    const height = (day.count / maxCount) * 100;
                    return (
                      <div
                        key={index}
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: `${height}%`,
                            backgroundColor: 'var(--accent-primary)',
                            borderRadius: '4px 4px 0 0',
                            minHeight: '4px'
                          }}
                          title={`${day.date}: ${day.count} loads`}
                        />
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.65rem', transform: 'rotate(-45deg)' }}>
                          {localDateFromYMD(day.date).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : activeTab === 'customers' ? (
            <div>
              <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0' }}>Top Customers by Load Volume</h3>
              {stats.topCustomers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                  No customer data available for this period
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {stats.topCustomers.map((customer, index) => {
                    const maxCount = stats.topCustomers[0]?.count || 1;
                    const percentage = (customer.count / maxCount) * 100;
                    return (
                      <div
                        key={index}
                        style={{
                          backgroundColor: 'var(--surface-2)',
                          borderRadius: '8px',
                          padding: '1rem',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            width: `${percentage}%`,
                            backgroundColor: 'rgba(22, 199, 154, 0.2)',
                            transition: 'width 0.3s ease'
                          }}
                        />
                        <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <span style={{
                              backgroundColor: 'var(--accent-primary)',
                              color: 'var(--text-primary)',
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '0.85rem'
                            }}>
                              {index + 1}
                            </span>
                            <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>{customer.name}</span>
                          </div>
                          <span style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>
                            {customer.count} load{customer.count !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : activeTab === 'drivers' ? (
            <div>
              <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0' }}>Driver Performance</h3>
              {stats.driverPerformance.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                  No driver data available for this period
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Driver</th>
                      <th style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--accent-primary)' }}>Total Loads</th>
                      <th style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--accent-primary)' }}>Completed</th>
                      <th style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--accent-primary)' }}>Completion Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.driverPerformance.map((driver, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid var(--surface-2)' }}>
                        <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '1.2rem' }}>&#128100;</span>
                            {driver.name}
                          </div>
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-primary)' }}>
                          {driver.total}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--color-success)' }}>
                          {driver.completed}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                          <div style={{
                            display: 'inline-block',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '20px',
                            backgroundColor: driver.completionRate >= 80 ? 'var(--color-success)' : driver.completionRate >= 50 ? 'var(--color-warning)' : 'var(--color-danger)',
                            color: 'var(--text-primary)',
                            fontWeight: 'bold'
                          }}>
                            {driver.completionRate}%
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            /* Owner Oversight tab */
            <div>
              <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0' }}>AI Guessed Fields Report</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Loads where the AI had to guess or infer field values from WhatsApp messages
              </p>
              
              {stats.guessedFields.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>&#9989;</div>
                  <p>No guessed fields in this period - all loads had complete information!</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {stats.guessedFields.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: 'var(--surface-2)',
                        borderRadius: '8px',
                        padding: '1rem',
                        borderLeft: '4px solid var(--color-warning)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                          Load {item.loadNumber}
                        </span>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                          {new Date(item.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {item.fields.map((field, i) => (
                          <span
                            key={i}
                            style={{
                              backgroundColor: 'var(--color-warning)',
                              color: 'var(--text-primary)',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.8rem'
                            }}
                          >
                            {field}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0' }}>Dispatcher Activity Summary</h3>
                <div style={{
                  backgroundColor: 'var(--surface-2)',
                  borderRadius: '8px',
                  padding: '1rem',
                  textAlign: 'center',
                  color: 'var(--text-muted)'
                }}>
                  <p>Dispatcher activity logging will be available in a future update.</p>
                  <p style={{ fontSize: '0.85rem' }}>This will track load creation, edits, and assignment changes.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      {content}
    </div>
  );
}

export default ReportsModal;
